import gql from 'graphql-tag';

import {
  AlbumDetails,
  AlbumPrices,
  AlbumListingDetails,
  PhotoThumbnails,
  PhotoUrlTypeFields,
  AlbumWatermarkConfig,
  AlbumForStatusItems,
  AlbumSlideshowDetails,
  AlbumSlideshowPhotosDetails,
  AlbumSlideshowOwnerPhotosDetails,
  AlbumSlideshowLayoutsDetails,
  AlbumSlideshowVideoDetails,
  AlbumHierarchyGradeDetails,
  AlbumLogoImageConfig,
} from '../fragments/album';
import { AlbumPhotoGroupBasicDetails } from '../fragments/album-photo-group';
import { AutoContributorSettingsDetails } from '../fragments/contributor-assignments';
import {
  AlbumFolderEventSettingsFields,
  AlbumFolderForReviewDetails,
  AlbumFolderMainCoverPhoto,
  AlbumFolderPendingCustomPrintDetails,
} from '../fragments/album-folder';

export const albumCreate = gql`
  mutation albumCreate($input: AlbumCreateInput!) {
    albumCreate(input: $input) {
      album {
        ...AlbumDetails
        ...AlbumListingDetails
        ...AlbumForStatusItems
        ...AlbumLogoImageConfig
        album_folder {
          id
          name
          album_type
          album_subtype
          join_code
          is_demo
          picture_day_enabled
          individual_albums
          photo_sharing_mode
          album_photos {
            total
          }
          album_photos_count
          parent {
            id
          }
          creator: account {
            id
          }
          cover_photos(slice: { limit: 1 }) {
            records {
              id
              faces_box
              thumbnail_urls(ignore_watermark: true) {
                ...PhotoUrlTypeFields
              }
            }
          }
          has_children
          has_album
          upload_mode
          event_settings {
            ...AlbumFolderEventSettingsFields
          }
          ...AlbumFolderPendingCustomPrintDetails
          ...AlbumFolderMainCoverPhoto
          ...AlbumFolderForReviewDetails
        }
        album_photo_groups(slice: { limit: 999 }) {
          total
          records {
            ...AlbumPhotoGroupBasicDetails
          }
        }
      }
    }
  }
  ${AlbumDetails}
  ${AlbumListingDetails}
  ${AlbumForStatusItems}
  ${AlbumLogoImageConfig}
  ${AlbumPhotoGroupBasicDetails}
  ${AlbumFolderMainCoverPhoto}
  ${AlbumFolderForReviewDetails}
  ${AlbumFolderPendingCustomPrintDetails}
  ${AlbumFolderEventSettingsFields}
`;

export const albumUpdate = gql`
  mutation albumUpdate($input: AlbumUpdateInput!) {
    albumUpdate(input: $input) {
      album {
        ...AlbumDetails
        ...AlbumPrices
        ...AlbumWatermarkConfig
        ...AlbumHierarchyGradeDetails
        ...AlbumLogoImageConfig
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
        root_album_folder {
          id
          event_settings {
            ...AlbumFolderEventSettingsFields
          }
        }
      }
    }
  }
  ${AlbumDetails}
  ${AlbumPrices}
  ${AlbumWatermarkConfig}
  ${AutoContributorSettingsDetails}
  ${AlbumFolderEventSettingsFields}
  ${AlbumHierarchyGradeDetails}
  ${AlbumLogoImageConfig}
`;

export const albumDelete = gql`
  mutation albumDelete($input: AlbumDeleteInput!) {
    albumDelete(input: $input) {
      deleted
    }
  }
`;

export const albumAddPhotos = gql`
  mutation albumAddPhotos($input: AlbumAddPhotoInput!) {
    albumAddPhotos(input: $input) {
      mutationId
      album {
        id
        photos(include_unprocessed: true, slice: { limit: 25, offset: 0 }) {
          total
          records {
            thumbnail_urls(ignore_watermark: true) {
              ...PhotoUrlTypeFields
            }
            watermarked_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
        photos_ungrouped: photos(
          include_unprocessed: true
          ungrouped_only: true
          slice: { limit: 25, offset: 0 }
          album_photo_filter: PUBLISHED
        ) {
          total
          count
          records {
            id
            thumbnail_urls(ignore_watermark: true) {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const albumPhotosDelete = gql`
  mutation albumPhotosDelete($input: AlbumPhotosDeleteInput!) {
    albumPhotosDelete(input: $input) {
      success
    }
  }
`;

export const albumHighlightCreate = gql`
  mutation albumHighlightCreate($input: AlbumHighlightCreateInput!) {
    albumHighlightCreate(input: $input) {
      album_highlight {
        id
        photo {
          ...PhotoThumbnails
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const albumHighlightDelete = gql`
  mutation albumHighlightDelete($input: AlbumHighlightDeleteInput!) {
    albumHighlightDelete(input: $input) {
      deleted
    }
  }
`;

export const albumsJoinByCode = gql`
  mutation albumsJoinByCode($input: AlbumsJoinByCodeInput!) {
    albumsJoinByCode(input: $input) {
      album_memberships {
        count
      }
    }
  }
`;

export const joinByCode = gql`
  mutation joinByCode($input: JoinByCodeInput!) {
    joinByCode(input: $input) {
      album_membership {
        id
      }
      album_memberships {
        id
      }
    }
  }
`;

export const albumPhotosHide = gql`
  mutation albumPhotosHide($input: AlbumPhotosHideInput!, $albumId: RecordID!) {
    albumPhotosHide(input: $input) {
      photos {
        id
        album_photo_status(album_id: $albumId)
        thumbnail_urls(ignore_watermark: true) {
          ...PhotoUrlTypeFields
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const albumPhotosPublish = gql`
  mutation albumPhotosPublish(
    $input: AlbumPhotosPublishInput!
    $albumId: RecordID!
  ) {
    albumPhotosPublish(input: $input) {
      photos {
        id
        album_photo_status(album_id: $albumId)
        thumbnail_urls(ignore_watermark: true) {
          ...PhotoUrlTypeFields
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const faceHighlightsUpdate = gql`
  mutation faceHighlightsUpdate($input: FaceHighlightsUpdateInput!) {
    faceHighlightsUpdate(input: $input) {
      face_highlights {
        id
        rating
        face {
          id
          facebox
          matched_photos(slice: { limit: 1 }) {
            records {
              id
              identity {
                id
                name
                current_photo {
                  ...PhotoThumbnails
                }
              }
            }
          }
        }
        album {
          id
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const albumPhotosMove = gql`
  mutation albumPhotosMove($input: AlbumPhotosMoveInput!) {
    albumPhotosMove(input: $input) {
      success
    }
  }
`;

export const albumSlideshowUpdate = gql`
  mutation albumSlideshowUpdate(
    $input: AlbumUpdateInput!
    $offset: Int = 0
    $limit: Int = 10
  ) {
    albumUpdate(input: $input) {
      album {
        id
        name
        auto_contributor_settings {
          view_access
          upload_access
        }
        auto_contributor_share_url
        creator {
          id
          logo_image {
            id
            url
          }
        }
        slideshow {
          ...AlbumSlideshowDetails
          ...AlbumSlideshowPhotosDetails
          ...AlbumSlideshowOwnerPhotosDetails
          ...AlbumSlideshowLayoutsDetails
        }
      }
    }
  }
  ${AlbumSlideshowDetails}
  ${AlbumSlideshowPhotosDetails}
  ${AlbumSlideshowOwnerPhotosDetails}
  ${AlbumSlideshowLayoutsDetails}
`;

export const albumVideoSlideshowUpdate = gql`
  mutation albumVideoSlideshowUpdate(
    $input: AlbumUpdateInput!
    $offset: Int = 0
    $limit: Int = 10
  ) {
    albumUpdate(input: $input) {
      album {
        id
        name
        auto_contributor_settings {
          view_access
          upload_access
        }
        auto_contributor_share_url
        creator {
          id
          logo_image {
            id
            url
          }
        }
        slideshow {
          ...AlbumSlideshowVideoDetails
        }
      }
    }
  }
  ${AlbumSlideshowVideoDetails}
`;

export const albumSlideshowContributeSend = gql`
  mutation albumSlideshowContributeSend(
    $input: AlbumSlideshowContributeSendInput!
  ) {
    albumSlideshowContributeSend(input: $input) {
      success
    }
  }
`;

export const albumsUnjoin = gql`
  mutation albumsUnjoin($input: AlbumsUnjoinInput!) {
    albumsUnjoin(input: $input) {
      success
    }
  }
`;
